import { useEffect, useState } from "react";
import { Menu, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { scrollToId } from "@/lib/scroll";

const NAV_LINKS = [
  { id: "accueil", label: "Accueil" },
  { id: "services", label: "Services" },
  { id: "pourquoi", label: "Pourquoi nous choisir" },
  { id: "realisations", label: "Avant / Après" },
  { id: "contact", label: "Contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "border-b border-slate-200/80 bg-white/90 shadow-[0_8px_30px_rgba(15,23,42,0.05)] backdrop-blur-md"
          : "border-b border-transparent bg-white/70 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16">
        <a
          href="#accueil"
          onClick={go("accueil")}
          data-testid="header-logo"
          className="group flex items-center gap-3"
          aria-label="Zion Solutions Suisse Sàrl — Accueil"
        >
          <img
            src="/logo-icon.png"
            alt=""
            width="44"
            height="44"
            className="h-10 w-10 shrink-0 object-contain sm:h-11 sm:w-11"
          />
          <span className="flex flex-col leading-none">
            <span className="font-display text-[15px] font-semibold tracking-[0.08em] text-ink sm:text-base">
              ZION SOLUTIONS
            </span>
            <span className="mt-1 text-[10px] font-medium uppercase tracking-[0.34em] text-brand">
              Suisse Sàrl
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Navigation principale">
          {NAV_LINKS.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              onClick={go(link.id)}
              data-testid={`nav-link-${link.id}`}
              className="relative text-sm font-medium text-slate-600 transition-colors duration-200 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-brand after:transition-[width] after:duration-300 hover:text-ink hover:after:w-full"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="tel:+41782135944"
            data-testid="header-call-button"
            className="hidden items-center gap-2 text-sm font-medium text-slate-700 transition-colors hover:text-brand md:flex"
            aria-label="Appeler le +41 78 213 59 44"
          >
            <Phone className="h-4 w-4 text-brand" strokeWidth={1.8} />
            <span className="hidden xl:inline">+41 78 213 59 44</span>
          </a>
          <button
            type="button"
            onClick={go("devis")}
            data-testid="header-cta-button"
            className="hidden rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-[background-color,color,transform] duration-200 hover:bg-sun hover:text-ink active:scale-[0.98] sm:block"
          >
            Demander un devis
          </button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                data-testid="mobile-menu-button"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-ink lg:hidden"
                aria-label="Ouvrir le menu"
              >
                <Menu className="h-5 w-5" strokeWidth={1.8} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-white">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <nav className="mt-10 flex flex-col gap-1" aria-label="Navigation mobile">
                {NAV_LINKS.map((link) => (
                  <a
                    key={link.id}
                    href={`#${link.id}`}
                    onClick={go(link.id)}
                    data-testid={`mobile-nav-link-${link.id}`}
                    className="rounded-lg px-4 py-3 text-base font-medium text-slate-700 transition-colors hover:bg-mist hover:text-brand"
                  >
                    {link.label}
                  </a>
                ))}
                <button
                  type="button"
                  onClick={go("devis")}
                  data-testid="mobile-cta-button"
                  className="mt-4 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-deep"
                >
                  Demander un devis
                </button>
                <a
                  href="tel:+41782135944"
                  data-testid="mobile-call-button"
                  className="mt-2 flex items-center justify-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-ink"
                >
                  <Phone className="h-4 w-4 text-brand" strokeWidth={1.8} />
                  Nous appeler
                </a>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
